import { Link } from 'react-router-dom';

/**
 * The section tile grid on the Today screen. Two variants:
 *
 *  - `big`   — large tiles (home layout "tiles"). Leads with a live
 *              Obligations tile (links to /obligations), followed by
 *              the not-yet-built domains.
 *  - `small` — compact tiles under a "Jump to" label (home layout
 *              "summary"), shown beneath the obligations panel.
 *
 * Obligations, Documents and People are real routes today; they render
 * as live tiles linking to their pages. Every other domain (Vault,
 * Inventory, Travel, Archive) renders as a non-interactive "Coming
 * soon" tile so the grid never offers a dead link — each lights up as
 * its domain is built. See IDEAS.md "Home layout".
 */

type IconName =
  | 'documents'
  | 'vault'
  | 'budget'
  | 'inventory'
  | 'travel'
  | 'archive'
  | 'people'
  | 'obligations';

function Icon({ name }: { name: IconName }) {
  // Shared stroke style; paths are 24×24, sized by CSS.
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.9,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    viewBox: '0 0 24 24',
    'aria-hidden': true,
  };
  switch (name) {
    case 'obligations':
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M3 10h18M8 2v4M16 2v4M8.5 16l2 2 4-4" />
        </svg>
      );
    case 'documents':
      return (
        <svg {...common}>
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          <path d="M14 3v5h5M9 13h6M9 17h6" />
        </svg>
      );
    case 'vault':
      return (
        <svg {...common}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="M9 11.5l2 2 4-4" />
        </svg>
      );
    case 'budget':
      return (
        <svg {...common}>
          <rect x="3" y="7" width="18" height="10" rx="2" />
          <circle cx="12" cy="12" r="2.5" />
          <path d="M6.5 12h.01M17.5 12h.01" />
        </svg>
      );
    case 'inventory':
      return (
        <svg {...common}>
          <path d="M12 2 3 7v10l9 5 9-5V7z" />
          <path d="M3 7l9 5 9-5M12 12v10" />
        </svg>
      );
    case 'travel':
      return (
        <svg {...common}>
          <rect x="5" y="7" width="14" height="13" rx="2" />
          <path d="M9 7V4h6v3M5 13h14" />
        </svg>
      );
    case 'archive':
      return (
        <svg {...common}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="M21 15l-5-5L5 21" />
        </svg>
      );
    case 'people':
      return (
        <svg {...common}>
          <circle cx="9" cy="8" r="3.2" />
          <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6M16 4.5a3.2 3.2 0 0 1 0 7M21 20c0-2.5-1.5-4.7-3.7-5.6" />
        </svg>
      );
  }
}

interface SectionDef {
  key: IconName;
  title: string;
}

// Order mirrors the approved mockups (design-mockups-AB.html).
const DOMAINS: SectionDef[] = [
  { key: 'documents', title: 'Documents' },
  { key: 'vault', title: 'Vault' },
  { key: 'budget', title: 'Budget' },
  { key: 'inventory', title: 'Inventory' },
  { key: 'travel', title: 'Travel' },
  { key: 'archive', title: 'Archive' },
  { key: 'people', title: 'People' },
];

function SoonTile({ section, small }: { section: SectionDef; small?: boolean }) {
  return (
    <div
      className={`home-tile home-tile--${section.key} home-tile--soon${small ? ' home-tile--small' : ''}`}
      aria-disabled="true"
    >
      <span className="home-tile__chip">
        <Icon name={section.key} />
      </span>
      <span className="home-tile__body">
        <span className="home-tile__title">{section.title}</span>
        <span className="home-tile__sub">Coming soon</span>
      </span>
      <span className="home-tile__badge">Soon</span>
    </div>
  );
}

function LiveTile({
  section,
  to,
  sub,
  small,
}: {
  section: SectionDef;
  to: string;
  sub?: string;
  small?: boolean;
}) {
  return (
    <Link
      to={to}
      className={`home-tile home-tile--${section.key}${small ? ' home-tile--small' : ''}`}
    >
      <span className="home-tile__chip">
        <Icon name={section.key} />
      </span>
      <span className="home-tile__body">
        <span className="home-tile__title">{section.title}</span>
        <span className="home-tile__sub">{sub ?? 'View all'}</span>
      </span>
    </Link>
  );
}

export function SectionTiles({
  variant,
  obligationsSummary,
  documentsSummary,
  peopleSummary,
  vaultSummary,
  budgetSummary,
  travelSummary,
}: {
  variant: 'big' | 'small';
  obligationsSummary?: string;
  documentsSummary?: string;
  peopleSummary?: string;
  vaultSummary?: string;
  budgetSummary?: string;
  travelSummary?: string;
}) {
  // A built domain renders as a live tile; everything else as a
  // "Coming soon" placeholder. Keeps the per-domain wiring in one spot.
  function tile(d: SectionDef, small?: boolean) {
    if (d.key === 'documents') {
      return <LiveTile key={d.key} section={d} to="/documents" sub={documentsSummary} small={small} />;
    }
    if (d.key === 'people') {
      return <LiveTile key={d.key} section={d} to="/people" sub={peopleSummary} small={small} />;
    }
    if (d.key === 'vault') {
      return <LiveTile key={d.key} section={d} to="/vault" sub={vaultSummary} small={small} />;
    }
    if (d.key === 'budget') {
      return <LiveTile key={d.key} section={d} to="/budget" sub={budgetSummary} small={small} />;
    }
    if (d.key === 'travel') {
      return <LiveTile key={d.key} section={d} to="/travel" sub={travelSummary} small={small} />;
    }
    return <SoonTile key={d.key} section={d} small={small} />;
  }

  if (variant === 'big') {
    // Big grid: live Obligations tile + the other domains. People used
    // to stay out of this layout while it was only a "Coming soon"
    // placeholder; now it's a real page it earns its tile here too —
    // otherwise the tiles-only home would have no way to reach it.
    return (
      <div className="home-grid">
        <Link to="/obligations" className="home-tile home-tile--obligations">
          <span className="home-tile__chip">
            <Icon name="obligations" />
          </span>
          <span className="home-tile__body">
            <span className="home-tile__title">Obligations</span>
            <span className="home-tile__sub">{obligationsSummary ?? 'View all'}</span>
          </span>
        </Link>
        {DOMAINS.map((d) => tile(d))}
      </div>
    );
  }

  // Small grid: every section tile under a "Jump to" label — shown
  // beside/beneath the panels in the summary layout. Obligations leads:
  // the panel above already carries its detail, but omitting the tile
  // here while the tiles layout shows one read as an inconsistency.
  return (
    <>
      <div className="section__label home-tiles__label">Jump to</div>
      <div className="home-grid home-grid--small">
        <LiveTile
          section={{ key: 'obligations', title: 'Obligations' }}
          to="/obligations"
          sub={obligationsSummary}
          small
        />
        {DOMAINS.map((d) => tile(d, true))}
      </div>
    </>
  );
}
